import {
	Alert,
	Box,
	Button,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { ButtonLink } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo, useState } from "react"
import {
	type TWhatsAppMessage,
	useFetchWhatsAppInboxThread,
	useThreadMessages,
	WhatsAppThreadXHR,
} from "./store"
import { WhatsAppThreadContext } from "./Context"
import { WhatsAppConversationHeader } from "./conversation/ConversationHeader"
import { WhatsAppMessageList } from "./conversation/MessageList"
import { WhatsAppMessageComposer } from "./conversation/MessageComposer"
import { SendThreadTemplateDialog } from "./conversation/SendTemplateDialog"
import { SendQuickSendDialog } from "./conversation/SendQuickSendDialog"
import { useThreadTyping } from "./conversation/useThreadTyping"
import { useMarkThreadRead } from "./conversation/useMarkThreadRead"
import { isOutsideCustomerServiceWindow } from "./conversation/utils"
import { WhatsAppTypingIndicator } from "./conversation/TypingIndicator"
import { notifyInbound } from "./notifications"
import { useAuthUser } from "../Auth"
import { generatePath } from "../router-utils"
import {
	trackWhatsAppMessageSent,
	type TrackingWhatsAppSurface,
} from "@sembark-travel/tracking"

export function WhatsAppInboxThread({
	threadId,
	openBoundary,
	openUnread,
	backTo = generatePath("/whatsapp/inbox"),
	trackingSurface = "inbox",
}: {
	threadId: number
	// Open-time read pointer + unread count, passed from the list click so the
	// unread divider doesn't depend on the detail's SWR cache freshness.
	openBoundary?: number | null
	openUnread?: number
	// Where the mobile back button returns to. The trip's WhatsApp tab renders
	// this same view, and going "back" there means the trip's thread list, not
	// the global inbox.
	backTo?: string
	// Which part of the app this view is embedded in, for analytics.
	trackingSurface?: TrackingWhatsAppSurface
}) {
	const xhr = useXHR()
	const actions = useMemo(() => WhatsAppThreadXHR(xhr), [xhr])
	// Thread metadata (header, context pane, unread pointer) — no longer carries
	// the message history.
	const { thread, error, isLoading, refresh } =
		useFetchWhatsAppInboxThread(threadId)
	// The message history: paginated, and kept live over websockets (a new-message
	// broadcast pulls only the delta, a status broadcast patches in place).
	// `onActivity` refreshes the thread metadata that feeds the right context pane
	// (message count, recent activity) whenever a new message lands.
	const { messages, loadOlder, hasMoreOlder, isLoadingOlder, syncNew } =
		useThreadMessages(threadId, {
			onActivity: refresh,
			// The operator is viewing this thread, so a new inbound is already on
			// screen — stay silent while the tab is focused. If the tab is in the
			// background (they stepped away with it open), still ding + badge.
			onInbound: () => {
				if (document.hidden) {
					notifyInbound()
				}
			},
			// Same rule as an inbound message: silent while they are looking at
			// the thread, since the reaction appears on screen either way.
			onReaction: () => {
				if (document.hidden) {
					notifyInbound()
				}
			},
		})
	// "X is typing…" over the same private channel, driven by Echo client events
	// (whispers) — no backend. Seeded threads have several subscribers, so this
	// flags when another of them is already replying.
	const { user } = useAuthUser()
	const currentUser = useMemo(
		() => (user ? { id: user.id, name: user.name } : null),
		[user]
	)
	const { typingUsers, notifyTyping, stopTyping } = useThreadTyping(
		threadId,
		currentUser,
		user?.tenant_id
	)
	const { lg, xl } = useBreakpoints()
	const isDesktop = lg || xl
	const [isContextOpen, openContext, closeContext] = useDialog()
	const [isTemplateDialogOpen, openTemplateDialog, closeTemplateDialog] =
		useDialog()
	const [isQuickSendDialogOpen, openQuickSendDialog, closeQuickSendDialog] =
		useDialog()

	// The operator is actively reading THIS conversation — heartbeat with its
	// thread id so its own new-message notifications are suppressed while open.
	useEffect(() => {
		actions.presence(threadId).catch(() => undefined)
		const timer = window.setInterval(() => {
			actions.presence(threadId).catch(() => undefined)
		}, 30000)
		return () => window.clearInterval(timer)
	}, [actions, threadId])

	// Bumped after a send to scroll the message list to the newest message.
	const [scrollToBottomSignal, setScrollToBottomSignal] = useState(0)

	// The message the operator is replying to. Picking another replaces it.
	// Kept with its thread so switching conversations drops it.
	const [replying, setReplying] = useState<{
		threadId: number
		message: TWhatsAppMessage
	} | null>(null)
	const replyTo = replying?.threadId === threadId ? replying.message : null
	function replyToMessage(message: TWhatsAppMessage) {
		setReplying({ threadId, message })
	}
	function cancelReply() {
		setReplying(null)
	}

	// In the inbox, having the conversation open is reading it.
	useMarkThreadRead(thread, refresh)

	if (isLoading) {
		return <Spinner alignCenter padding="4" />
	}

	if (error || !thread) {
		return (
			<Alert status="error" title="Unable to load this conversation">
				It may have been removed or you no longer have access to it.
			</Alert>
		)
	}

	const outsideServiceWindow = isOutsideCustomerServiceWindow(
		thread.last_inbound_message_at
	)

	const composer = (
		<WhatsAppMessageComposer
			extraActions={
				<>
					<Button
						type="button"
						level="tertiary"
						inline
						size="sm"
						onClick={openTemplateDialog}
						disabled={!thread.phone_number?.id}
					>
						<Icons.DocumentText /> Use Template
					</Button>
					<Button
						type="button"
						level="tertiary"
						inline
						size="sm"
						onClick={openQuickSendDialog}
						disabled={!thread.phone_number?.id}
					>
						<Icons.LightningBolt /> Quick Send
					</Button>
				</>
			}
			sealed={Boolean(thread.contact?.identity_changed_at)}
			outsideServiceWindow={outsideServiceWindow}
			typingIndicator={<WhatsAppTypingIndicator typingUsers={typingUsers} />}
			onType={notifyTyping}
			onStopTyping={stopTyping}
			replyTo={replyTo}
			onCancelReply={cancelReply}
			onSend={async ({ message, attachment, hasAttachmentField }) => {
				if (attachment && hasAttachmentField) {
					const data = new FormData()
					if (message) {
						data.append("custom_message", message)
					}
					data.append("attachment", attachment)
					if (replyTo) {
						data.append("reply_to_message_id", String(replyTo.id))
					}
					await actions.send(thread.id, data)
					trackWhatsAppMessageSent({
						kind: "attachment",
						surface: trackingSurface,
					})
				} else {
					await actions.send(thread.id, {
						custom_message: message,
						reply_to_message_id: replyTo?.id,
					})
					trackWhatsAppMessageSent({ kind: "text", surface: trackingSurface })
				}
				cancelReply()
			}}
			onSent={async () => {
				// Pull just the sent message (no full refetch), then scroll to it.
				await syncNew()
				setScrollToBottomSignal((n) => n + 1)
			}}
		/>
	)

	// Rendered beside the composer so it follows whichever layout is on screen,
	// rather than being duplicated across both branches.
	const composerWithTemplates = (
		<>
			{composer}
			{thread ? (
				<SendThreadTemplateDialog
					open={isTemplateDialogOpen}
					onClose={closeTemplateDialog}
					thread={thread}
					onSend={async (payload) => {
						await actions.send(thread.id, payload)
						trackWhatsAppMessageSent({
							kind: "template",
							surface: trackingSurface,
						})
					}}
					onSent={async () => {
						await syncNew()
						setScrollToBottomSignal((n) => n + 1)
					}}
				/>
			) : null}
			{thread ? (
				<SendQuickSendDialog
					open={isQuickSendDialogOpen}
					onClose={closeQuickSendDialog}
					recipient={
						thread.contact?.name || thread.contact?.contact_number || ""
					}
					outsideServiceWindow={outsideServiceWindow}
					onSend={async ({ wa_quick_send_id }) => {
						const result = await actions.sendQuickSend(
							thread.id,
							wa_quick_send_id
						)
						trackWhatsAppMessageSent({
							kind: "quick_send",
							surface: trackingSurface,
						})
						return result
					}}
					onSent={async () => {
						await syncNew()
						setScrollToBottomSignal((n) => n + 1)
					}}
				/>
			) : null}
		</>
	)

	if (!isDesktop) {
		return (
			<Stack height="full" style={{ minHeight: 0 }}>
				<Inline
					paddingX="3"
					paddingY="2"
					borderBottomWidth="1"
					bgColor="subtle"
					alignItems="center"
					justifyContent="between"
					gap="2"
				>
					<ButtonLink to={backTo} level="tertiary" inline>
						<Icons.ArrowLeft /> Back
					</ButtonLink>
					<Text fontWeight="semibold" fontSize="sm">
						{thread.contact?.name ||
							thread.contact?.contact_number ||
							"Customer"}
					</Text>
					<Button
						level="tertiary"
						inline
						onClick={openContext}
						title="Conversation details"
					>
						<Icons.Users />
					</Button>
				</Inline>
				<WhatsAppMessageList
					thread={thread}
					messages={messages}
					onRefresh={refresh}
					onLoadOlder={loadOlder}
					hasMoreOlder={hasMoreOlder}
					isLoadingOlder={isLoadingOlder}
					openBoundary={openBoundary}
					openUnread={openUnread}
					scrollToBottomSignal={scrollToBottomSignal}
					onReply={replyToMessage}
					fillHeight
				/>
				<Box borderTopWidth="1" paddingX="4" paddingY="2" bgColor="default">
					{composerWithTemplates}
				</Box>
				<Dialog open={isContextOpen} onClose={closeContext}>
					<Dialog.Header>
						<Dialog.Title>Conversation details</Dialog.Title>
					</Dialog.Header>
					<Dialog.Body>
						<WhatsAppThreadContext thread={thread} onRefresh={refresh} />
					</Dialog.Body>
				</Dialog>
			</Stack>
		)
	}

	return (
		<Box
			display="grid"
			height="full"
			style={{
				gridTemplateColumns: "minmax(0, 1fr) 340px",
				minHeight: 0,
			}}
		>
			<Box borderRightWidth="1" style={{ minHeight: 0, overflow: "hidden" }}>
				<Stack height="full" style={{ minHeight: 0 }}>
					<WhatsAppConversationHeader thread={thread} />
					<WhatsAppMessageList
						thread={thread}
						messages={messages}
						onRefresh={refresh}
						onLoadOlder={loadOlder}
						hasMoreOlder={hasMoreOlder}
						isLoadingOlder={isLoadingOlder}
						openBoundary={openBoundary}
						openUnread={openUnread}
						scrollToBottomSignal={scrollToBottomSignal}
						onReply={replyToMessage}
						fillHeight
					/>
					<Box borderTopWidth="1" paddingX="4" paddingY="2" bgColor="default">
						{composerWithTemplates}
					</Box>
				</Stack>
			</Box>
			<Box style={{ minHeight: 0, overflowY: "auto" }} bgColor="subtle">
				<WhatsAppThreadContext thread={thread} onRefresh={refresh} />
			</Box>
		</Box>
	)
}
